/**
 * ClientIntegrationMapper.tsx
 *
 * A universal, mode-driven modal for mapping Quire clients to
 * QuickBooks Online customers and/or Xero contacts.
 *
 * Modes:
 *   - bulk:   Full table of all clients, one column per connected provider (integrations page)
 *   - single: Compact card for one client with stacked provider dropdowns (client detail page)
 *   - quick:  Unmapped clients first, then mapped (dashboard quick action)
 */

import { useEffect, useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "@/components/ui/toast";
import QuickBooksLogo from "@/components/svgs/quickbook.svg";
import XeroLogo from "@/components/svgs/xero.svg";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface ClientIntegrationMapperProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Rendering mode */
  mode: "bulk" | "single" | "quick";
  /** When mode='single', scope to this client */
  clientId?: string;
  /** When mode='single', display the client name in the header */
  clientName?: string;
  /** Callback after successful save (e.g. to refetch client detail) */
  onSaved?: () => void;
}

interface QboCustomer {
  Id: string;
  DisplayName: string;
  PrimaryEmailAddr?: { Address?: string };
}

interface XeroContact {
  ContactID: string;
  Name: string;
  EmailAddress?: string;
}

// Draft entry for one client
interface DraftMapping {
  qboCustomerId: string;
  xeroContactId: string;
}

// ── Helpers ───────────────────────────────────────────────────────────────────

function ProviderLogo({
  provider,
  size = 20,
}: {
  provider: "qbo" | "xero";
  size?: number;
}) {
  const src = provider === "qbo" ? QuickBooksLogo : XeroLogo;
  const alt = provider === "qbo" ? "QuickBooks" : "Xero";
  return (
    <img
      src={src}
      alt={alt}
      style={{ width: size, height: size }}
      className={`object-contain ${provider === "qbo" ? "dark:invert" : ""}`}
    />
  );
}

// ── Main Component ────────────────────────────────────────────────────────────

export function ClientIntegrationMapper({
  open,
  onOpenChange,
  mode,
  clientId,
  clientName,
  onSaved,
}: ClientIntegrationMapperProps) {
  // ── Data queries ──────────────────────────────────────────────────────────
  const integrations_q = trpc.integrations.list.useQuery(undefined, {
    enabled: open,
  });
  const clients_q = trpc.clients.list.useQuery(
    { status: "active" },
    { enabled: open },
  );
  const qboCustomers_q = trpc.integrations.listQboCustomers.useQuery(
    undefined,
    { enabled: open && Boolean(integrations_q.data?.qbo?.accountId) },
  );
  const xeroContacts_q = trpc.integrations.listXeroContacts.useQuery(
    undefined,
    { enabled: open && Boolean(integrations_q.data?.xero?.tenantId) },
  );
  const batchMap = trpc.integrations.batchMapClients.useMutation();

  // Provider connectivity
  const qboConnected = Boolean(integrations_q.data?.qbo?.accountId);
  const xeroConnected = Boolean(integrations_q.data?.xero?.tenantId);
  const anyConnected = qboConnected || xeroConnected;

  // ── Draft state ───────────────────────────────────────────────────────────
  const [draft, setDraft] = useState<Record<string, DraftMapping>>({});
  const [serverSnapshot, setServerSnapshot] = useState<
    Record<string, DraftMapping>
  >({});

  // Confirmation for smart-switching
  const [switchConfirm, setSwitchConfirm] = useState<{
    clientId: string;
    clientName: string;
    fromProvider: "QuickBooks" | "Xero";
    toProvider: "QuickBooks" | "Xero";
    newValue: string;
    field: "qboCustomerId" | "xeroContactId";
  } | null>(null);

  // Sync from server data
  useEffect(() => {
    if (open && clients_q.data) {
      const initial: Record<string, DraftMapping> = {};
      clients_q.data.forEach((c) => {
        initial[c.id] = {
          qboCustomerId: c.qboCustomerId || "",
          xeroContactId: c.xeroContactId || "",
        };
      });
      setDraft(initial);
      setServerSnapshot(initial);
    }
  }, [clients_q.data, open]);

  // ── Computed ──────────────────────────────────────────────────────────────

  // Filter clients based on mode
  const allClients = clients_q.data ?? [];
  const filteredClients = (() => {
    if (mode === "single" && clientId) {
      return allClients.filter((c) => c.id === clientId);
    }
    if (mode === "quick") {
      // Unmapped first, then mapped
      return [...allClients].sort((a, b) => {
        const aMapped = Boolean(a.qboCustomerId || a.xeroContactId);
        const bMapped = Boolean(b.qboCustomerId || b.xeroContactId);
        if (aMapped === bMapped) return 0;
        return aMapped ? 1 : -1;
      });
    }
    return allClients;
  })();

  // Unmapped count for quick mode header
  const unmappedCount = allClients.filter(
    (c) => !c.qboCustomerId && !c.xeroContactId,
  ).length;

  // Dirty check
  const isDirty = (() => {
    const allKeys = new Set([
      ...Object.keys(draft),
      ...Object.keys(serverSnapshot),
    ]);
    for (const k of allKeys) {
      const d = draft[k] || { qboCustomerId: "", xeroContactId: "" };
      const s = serverSnapshot[k] || { qboCustomerId: "", xeroContactId: "" };
      if (d.qboCustomerId !== s.qboCustomerId) return true;
      if (d.xeroContactId !== s.xeroContactId) return true;
    }
    return false;
  })();

  const qboCustomers: QboCustomer[] = qboCustomers_q.data || [];
  const xeroContacts: XeroContact[] = xeroContacts_q.data || [];
  const isLoading =
    clients_q.isLoading ||
    integrations_q.isLoading ||
    (qboConnected && qboCustomers_q.isLoading) ||
    (xeroConnected && xeroContacts_q.isLoading);

  // ── Handlers ──────────────────────────────────────────────────────────────

  const handleSelect = (
    cId: string,
    field: "qboCustomerId" | "xeroContactId",
    value: string,
  ) => {
    const currentDraft = draft[cId] || { qboCustomerId: "", xeroContactId: "" };
    const otherField =
      field === "qboCustomerId" ? "xeroContactId" : "qboCustomerId";
    const otherValue = currentDraft[otherField];

    // Smart-switch: if mapping to a new provider and already mapped to the other
    if (value && otherValue) {
      const client = allClients.find((c) => c.id === cId);
      setSwitchConfirm({
        clientId: cId,
        clientName: client?.name || "this client",
        fromProvider: field === "qboCustomerId" ? "Xero" : "QuickBooks",
        toProvider: field === "qboCustomerId" ? "QuickBooks" : "Xero",
        newValue: value,
        field,
      });
      return;
    }

    setDraft((prev) => {
      const current = prev[cId] || { qboCustomerId: "", xeroContactId: "" };
      return {
        ...prev,
        [cId]: {
          qboCustomerId: field === "qboCustomerId" ? value : current.qboCustomerId,
          xeroContactId: field === "xeroContactId" ? value : current.xeroContactId,
        },
      };
    });
  };

  const confirmSwitch = () => {
    if (!switchConfirm) return;
    const { clientId: cId, field } = switchConfirm;

    setDraft((prev) => {
      return {
        ...prev,
        [cId]: {
          qboCustomerId: field === "qboCustomerId" ? switchConfirm.newValue : "",
          xeroContactId: field === "xeroContactId" ? switchConfirm.newValue : "",
        },
      };
    });
    setSwitchConfirm(null);
  };

  const handleSave = async () => {
    const allKeys = new Set([
      ...Object.keys(draft),
      ...Object.keys(serverSnapshot),
    ]);
    const changes: {
      clientId: string;
      qboCustomerId?: string | null;
      xeroContactId?: string | null;
    }[] = [];

    for (const k of allKeys) {
      const d = draft[k] || { qboCustomerId: "", xeroContactId: "" };
      const s = serverSnapshot[k] || { qboCustomerId: "", xeroContactId: "" };
      const entry: {
        clientId: string;
        qboCustomerId?: string | null;
        xeroContactId?: string | null;
      } = { clientId: k };
      let changed = false;

      if (d.qboCustomerId !== s.qboCustomerId) {
        entry.qboCustomerId = d.qboCustomerId || null;
        changed = true;
      }
      if (d.xeroContactId !== s.xeroContactId) {
        entry.xeroContactId = d.xeroContactId || null;
        changed = true;
      }
      if (changed) changes.push(entry);
    }

    if (changes.length === 0) return;

    try {
      await batchMap.mutateAsync({ mappings: changes });
      toast.success(
        `${changes.length} client mapping${changes.length > 1 ? "s" : ""} updated.`,
      );
      clients_q.refetch();
      onSaved?.();
      onOpenChange(false);
    } catch (err: any) {
      toast.error(err.message || "Failed to save mappings.");
    }
  };

  // ── Header text based on mode ─────────────────────────────────────────────

  const headerTitle = {
    bulk: "Map Clients to Integrations",
    single: `Link ${clientName || "Client"} to Integration`,
    quick: "Connect Clients to Integrations",
  }[mode];

  const headerDescription = {
    bulk: "Map each Quire client to a QuickBooks customer or Xero contact. This links their records so data syncs correctly.",
    single: `Choose which accounting integration to link with ${clientName || "this client"}.`,
    quick: unmappedCount > 0
      ? `${unmappedCount} client${unmappedCount !== 1 ? "s" : ""} not yet linked to an integration. Unmapped clients are shown first.`
      : "All clients are mapped. You can adjust mappings below.",
  }[mode];

  // ── Render ────────────────────────────────────────────────────────────────

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={`rounded-none border-2 border-(--border) p-6 bg-card ${
            mode === "single" ? "max-w-[480px]" : "max-w-[720px]"
          }`}
        >
          <DialogHeader>
            <DialogTitle className="text-[14px] font-bold text-foreground flex items-center gap-2">
              {/* Link icon */}
              <svg
                width="14"
                height="14"
                viewBox="0 0 14 14"
                fill="none"
                className="text-muted-foreground"
              >
                <path
                  d="M6 8L8 6M4.5 9.5L2.7 11.3a1.5 1.5 0 01-2.1-2.1L2.5 7.3a1.5 1.5 0 012.1 0M9.5 4.5l1.8-1.8a1.5 1.5 0 012.1 2.1L11.5 6.7a1.5 1.5 0 01-2.1 0"
                  stroke="currentColor"
                  strokeWidth="1.2"
                  strokeLinecap="square"
                />
              </svg>
              {headerTitle}
            </DialogTitle>
            <DialogDescription className="text-[11px] text-muted-foreground mt-1">
              {headerDescription}
            </DialogDescription>
          </DialogHeader>

          {/* No integrations connected */}
          {!isLoading && !anyConnected && (
            <div className="my-4 p-8 text-center border border-(--border) bg-card">
              <div className="flex items-center justify-center gap-3 mb-3">
                <div className="w-8 h-8 border border-(--border) bg-white dark:bg-(--colors-stone-50) flex items-center justify-center p-1.5">
                  <ProviderLogo provider="qbo" size={16} />
                </div>
                <div className="w-8 h-8 border border-(--border) bg-white dark:bg-(--colors-stone-50) flex items-center justify-center p-1.5">
                  <ProviderLogo provider="xero" size={16} />
                </div>
              </div>
              <p className="text-[12px] font-semibold text-foreground mb-1">
                No accounting integrations connected
              </p>
              <p className="text-[10px] text-muted-foreground max-w-[280px] mx-auto">
                Connect QuickBooks Online or Xero in Settings → Integrations to
                start mapping clients.
              </p>
            </div>
          )}

          {/* Loading */}
          {isLoading && (
            <div className="my-4 p-8 text-center text-[11px] text-muted-foreground">
              <div className="flex items-center justify-center gap-2">
                <span className="w-3 h-3 border-2 border-foreground border-t-transparent rounded-full animate-spin" />
                Loading clients and integration data…
              </div>
            </div>
          )}

          {/* Main content */}
          {!isLoading && anyConnected && (
            <>
              {mode === "single" ? (
                /* ── Single-client compact card ────────────────────────── */
                <div className="my-4 space-y-3">
                  {qboConnected && (
                    <div className="border border-(--border) bg-card">
                      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-(--border) bg-(--colors-stone-50) dark:bg-(--colors-stone-100)">
                        <ProviderLogo provider="qbo" size={16} />
                        <span className="text-[11px] font-bold text-foreground">
                          QuickBooks Online
                        </span>
                      </div>
                      <div className="p-3">
                        <select
                          value={
                            draft[clientId || ""]?.qboCustomerId || ""
                          }
                          onChange={(e) =>
                            clientId &&
                            handleSelect(
                              clientId,
                              "qboCustomerId",
                              e.target.value,
                            )
                          }
                          className="w-full h-9 px-2 bg-card border border-(--border) text-[11px] text-foreground focus:outline-none focus:ring-1 focus:ring-foreground rounded-none"
                        >
                          <option value="">-- Not linked --</option>
                          {qboCustomers.map((cust) => (
                            <option key={cust.Id} value={cust.Id}>
                              {cust.DisplayName}{" "}
                              {cust.PrimaryEmailAddr?.Address
                                ? `(${cust.PrimaryEmailAddr.Address})`
                                : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}

                  {xeroConnected && (
                    <div className="border border-(--border) bg-card">
                      <div className="flex items-center gap-2 px-3 py-2.5 border-b border-(--border) bg-(--colors-stone-50) dark:bg-(--colors-stone-100)">
                        <ProviderLogo provider="xero" size={16} />
                        <span className="text-[11px] font-bold text-foreground">
                          Xero
                        </span>
                      </div>
                      <div className="p-3">
                        <select
                          value={
                            draft[clientId || ""]?.xeroContactId || ""
                          }
                          onChange={(e) =>
                            clientId &&
                            handleSelect(
                              clientId,
                              "xeroContactId",
                              e.target.value,
                            )
                          }
                          className="w-full h-9 px-2 bg-card border border-(--border) text-[11px] text-foreground focus:outline-none focus:ring-1 focus:ring-foreground rounded-none"
                        >
                          <option value="">-- Not linked --</option>
                          {xeroContacts.map((c) => (
                            <option key={c.ContactID} value={c.ContactID}>
                              {c.Name}{" "}
                              {c.EmailAddress ? `(${c.EmailAddress})` : ""}
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>
                  )}
                </div>
              ) : (
                /* ── Bulk / Quick table ─────────────────────────────────── */
                <div className="my-4 max-h-[400px] overflow-y-auto border border-(--border) bg-card">
                  {filteredClients.length === 0 ? (
                    <div className="p-6 text-center text-[11px] text-muted-foreground">
                      No active clients found.
                    </div>
                  ) : (
                    <table className="w-full text-left text-[11px]">
                      <thead>
                        <tr className="border-b border-(--border) bg-(--colors-stone-50) dark:bg-(--colors-stone-100) font-bold text-muted-foreground sticky top-0 z-10">
                          <th className="p-3">Quire Client</th>
                          {qboConnected && (
                            <th className="p-3">
                              <span className="inline-flex items-center gap-1.5">
                                <ProviderLogo provider="qbo" size={12} />
                                QuickBooks
                              </span>
                            </th>
                          )}
                          {xeroConnected && (
                            <th className="p-3">
                              <span className="inline-flex items-center gap-1.5">
                                <ProviderLogo provider="xero" size={12} />
                                Xero
                              </span>
                            </th>
                          )}
                        </tr>
                      </thead>
                      <tbody>
                        {/* Unmapped separator in quick mode */}
                        {mode === "quick" && unmappedCount > 0 && unmappedCount < allClients.length && (
                          <>
                            {filteredClients.map((client, idx) => {
                              const isMapped = Boolean(
                                client.qboCustomerId || client.xeroContactId,
                              );
                              const prevClient = idx > 0 ? filteredClients[idx - 1] : undefined;
                              const prevIsMapped = Boolean(
                                prevClient && (prevClient.qboCustomerId || prevClient.xeroContactId)
                              );

                              return (
                                <>
                                  {isMapped && !prevIsMapped && idx > 0 && (
                                    <tr key={`sep-${idx}`}>
                                      <td
                                        colSpan={
                                          1 +
                                          (qboConnected ? 1 : 0) +
                                          (xeroConnected ? 1 : 0)
                                        }
                                        className="px-3 py-2 text-[9px] font-bold uppercase tracking-[0.07em] text-muted-foreground bg-(--colors-stone-50) dark:bg-(--colors-stone-100) border-y border-(--border)"
                                      >
                                        Already linked
                                      </td>
                                    </tr>
                                  )}
                                  <ClientRow
                                    key={client.id}
                                    client={client}
                                    draft={draft}
                                    qboConnected={qboConnected}
                                    xeroConnected={xeroConnected}
                                    qboCustomers={qboCustomers}
                                    xeroContacts={xeroContacts}
                                    onSelect={handleSelect}
                                  />
                                </>
                              );
                            })}
                          </>
                        )}
                        {/* Non-quick or all same mapping status */}
                        {(mode !== "quick" || unmappedCount === 0 || unmappedCount === allClients.length) &&
                          filteredClients.map((client) => (
                            <ClientRow
                              key={client.id}
                              client={client}
                              draft={draft}
                              qboConnected={qboConnected}
                              xeroConnected={xeroConnected}
                              qboCustomers={qboCustomers}
                              xeroContacts={xeroContacts}
                              onSelect={handleSelect}
                            />
                          ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </>
          )}

          <DialogFooter>
            {isDirty ? (
              <button
                className="h-9 px-4 bg-foreground text-background text-[11px] font-bold hover:opacity-90 transition-opacity rounded-none disabled:opacity-40 cursor-pointer"
                onClick={handleSave}
                disabled={batchMap.isPending}
              >
                {batchMap.isPending
                  ? "Saving…"
                  : `Save ${mode === "single" ? "" : "Mappings"}`}
              </button>
            ) : (
              <button
                className="h-9 px-4 border border-(--border) bg-card text-foreground text-[11px] font-bold hover:bg-muted transition-colors rounded-none cursor-pointer"
                onClick={() => onOpenChange(false)}
              >
                Close
              </button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* ── Smart-switch confirmation dialog ─────────────────────────────── */}
      <Dialog
        open={Boolean(switchConfirm)}
        onOpenChange={(v) => !v && setSwitchConfirm(null)}
      >
        <DialogContent className="max-w-[400px] rounded-none border-2 border-(--border) p-6 bg-card">
          <DialogHeader>
            <DialogTitle className="text-[13px] font-bold text-foreground">
              Switch integration?
            </DialogTitle>
            <DialogDescription className="text-[11px] text-muted-foreground mt-1">
              <strong>{switchConfirm?.clientName}</strong> is currently mapped to{" "}
              <strong>{switchConfirm?.fromProvider}</strong>. This will unmap
              them from {switchConfirm?.fromProvider} and link to{" "}
              <strong>{switchConfirm?.toProvider}</strong> instead.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter className="mt-4">
            <button
              className="h-8 px-3 border border-(--border) bg-card text-foreground text-[11px] font-bold hover:bg-muted transition-colors rounded-none cursor-pointer"
              onClick={() => setSwitchConfirm(null)}
            >
              Cancel
            </button>
            <button
              className="h-8 px-3 bg-foreground text-background text-[11px] font-bold hover:opacity-90 transition-opacity rounded-none cursor-pointer"
              onClick={confirmSwitch}
            >
              Switch to {switchConfirm?.toProvider}
            </button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}

// ── Row sub-component ─────────────────────────────────────────────────────────

function ClientRow({
  client,
  draft,
  qboConnected,
  xeroConnected,
  qboCustomers,
  xeroContacts,
  onSelect,
}: {
  client: any;
  draft: Record<string, DraftMapping>;
  qboConnected: boolean;
  xeroConnected: boolean;
  qboCustomers: QboCustomer[];
  xeroContacts: XeroContact[];
  onSelect: (
    clientId: string,
    field: "qboCustomerId" | "xeroContactId",
    value: string,
  ) => void;
}) {
  const d = draft[client.id] || { qboCustomerId: "", xeroContactId: "" };

  return (
    <tr className="border-b border-(--border) hover:bg-muted/40">
      <td className="p-3">
        <div className="font-semibold text-foreground">{client.name}</div>
        <div className="text-muted-foreground text-[10px]">{client.email}</div>
      </td>

      {qboConnected && (
        <td className="p-3">
          <select
            value={d.qboCustomerId}
            onChange={(e) =>
              onSelect(client.id, "qboCustomerId", e.target.value)
            }
            className="w-full h-8 px-2 bg-card border border-(--border) text-[11px] text-foreground focus:outline-none focus:ring-1 focus:ring-foreground rounded-none"
          >
            <option value="">-- Not linked --</option>
            {qboCustomers.map((cust) => (
              <option key={cust.Id} value={cust.Id}>
                {cust.DisplayName}{" "}
                {cust.PrimaryEmailAddr?.Address
                  ? `(${cust.PrimaryEmailAddr.Address})`
                  : ""}
              </option>
            ))}
          </select>
        </td>
      )}

      {xeroConnected && (
        <td className="p-3">
          <select
            value={d.xeroContactId}
            onChange={(e) =>
              onSelect(client.id, "xeroContactId", e.target.value)
            }
            className="w-full h-8 px-2 bg-card border border-(--border) text-[11px] text-foreground focus:outline-none focus:ring-1 focus:ring-foreground rounded-none"
          >
            <option value="">-- Not linked --</option>
            {xeroContacts.map((c) => (
              <option key={c.ContactID} value={c.ContactID}>
                {c.Name} {c.EmailAddress ? `(${c.EmailAddress})` : ""}
              </option>
            ))}
          </select>
        </td>
      )}
    </tr>
  );
}
